import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Facility } from '../types';

interface LayoutProps {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

const Layout: React.FC<LayoutProps> = ({ user, onLogout, children, facilities, selectedFacility, onFacilityChange }) => {
  const location = useLocation();

  const canSelectFacility = ['admin', 'doctor'].includes(user.role);

  const navigation = [
    { name: 'Dashboard', href: '/' },
    { name: 'Patients', href: '/patients' },
    { name: 'Alerts', href: '/alerts' },
    ...((['admin', 'doctor', 'nurse'].includes(user.role)) ? [{ name: 'Devices', href: '/devices' }] : []),
    ...((['admin', 'nurse'].includes(user.role)) ? [{ name: 'Providers', href: '/providers' }] : []),
    ...(user.role === 'admin' ? [{ name: 'Facilities', href: '/facilities' }] : []),
  ];

  return (
    <div className="flex-1 bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-2xl font-bold text-primary-600">Edge-on-Age</h1>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      location.pathname === item.href
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {canSelectFacility && facilities && facilities.length > 0 && onFacilityChange && (
                <select
                  value={selectedFacility || user.facility.id}
                  onChange={(e) => onFacilityChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              )}
              <span className="text-sm text-gray-700">
                {user.first_name} {user.last_name} ({user.role})
              </span>
              <button
                onClick={onLogout}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
};

export default Layout;
